import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import PropTypes from 'prop-types'

import { Alert, Button } from '@mui/material'
import { useTheme } from '@mui/styles'
import { useMediaQuery } from '@mui/system'

import { RowFlexEnd } from 'styles/containers'
import { ErrorText, Link as ExternalLink } from 'styles/typography'
import { useAuth } from 'hooks/useAuth'
import { useSignInMutation } from 'hooks/sign-in'
import { syncSession } from 'hooks/sso'
import language from 'language'
import LoadingIndicator from 'components/LoadingIndicator'
import {
  Hero,
  HeroContent,
  HeroHeadline,
  HeroText,
  Base,
  Main,
  MainContent,
  MainTitle,
  Form,
  FormFooter,
  LogoContainer,
  Logo,
  LandingHeaderContainer
} from 'styles/v2/containers/landing'
import { Paragraph, StyledLink } from 'styles/v2/ui/typography'
import { Divider } from 'styles/v2/ui/divider'
import { FormInput } from 'components/Form/FormInput'

import LandingHeaderDesktop from 'components/landing/header-desktop'
import LandingHeaderMobile from 'components/landing/header-mobile'

const validationSchema = yup.object({
  email: yup.string().required('Email required'),
  password: yup.string().required('Password required')
})

const formDefaultValues = { email: '', password: '' }

const pageLanguage = language.pages.login

const LoginForm = ({ isUserNew }) => {
  const [isSubmitError, setIsSubmitError] = useState(false)
  const signInMutation = useSignInMutation()

  const {
    control: formControl,
    handleSubmit: validateInputs,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: formDefaultValues
  })

  const { isChecking, isLoggedIn, login } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isChecking && isLoggedIn) {
      navigate('/sites', { replace: true })
    }
  }, [isChecking, isLoggedIn, navigate])

  const handleSubmit = (formData) => {
    setIsSubmitError(false)

    signInMutation.mutate(formData, {
      onSuccess: async (data) => {
        if (!data?.token) return
        const user = {
          email: formData.email,
          name: data.username,
          organization: data.organization
        }
        await syncSession(data.token)
        login(data.token, user)
        navigate('/sites')
      },
      onError: (error) => {
        setIsSubmitError(true)
        toast.error(error?.response?.data?.error ?? 'Login failed')
      }
    })
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (isChecking) return <LoadingIndicator />

  return (
    <Base>
      <LandingHeaderContainer>
        <LogoContainer draggable='false'>
          <Link to='https://www.globalmangrovewatch.org/' target='_blank' rel='noreferrer'>
            <Logo src='/images/landing/logo.webp' />
          </Link>
        </LogoContainer>
        {!isMobile && <LandingHeaderDesktop />}
        {isMobile && <LandingHeaderMobile />}
      </LandingHeaderContainer>
      <Hero>
        <HeroContent>
          <HeroHeadline>Welcome to the Mangrove Restoration Tracker Tool</HeroHeadline>
          <HeroText variant='text-sm'>
            The MRTT is an open-access resource to support restoration practitioners. It provides a
            secure location to hold information across restoration planning, intervention and
            monitoring.{' '}
            <ExternalLink
              to='https://tnc.app.box.com/s/pspea7mm2m2uldrqvhahmvp9dck6mc06'
              rel='noreferrer'
              target='_blank'
              style={{ color: 'white' }}>
              Learn more
            </ExternalLink>
          </HeroText>
        </HeroContent>
      </Hero>
      <Main>
        <MainContent>
          <MainTitle>{pageLanguage.title}</MainTitle>
          {isUserNew ? (
            <Alert variant='outlined' severity='success'>
              {language.success.signup}
            </Alert>
          ) : null}
          <Form onSubmit={validateInputs(handleSubmit)}>
            <FormInput
              type='email'
              name='email'
              label={pageLanguage.email}
              placeholder='Enter your email'
              control={formControl}
              error={errors?.email?.message}
            />
            <FormInput
              type='password'
              name='password'
              label={pageLanguage.password}
              placeholder='Enter your password'
              control={formControl}
              error={errors?.password?.message}
            />

            <RowFlexEnd>
              {isSubmitError && <ErrorText>{language.error.submit}</ErrorText>}
            </RowFlexEnd>
            <Button type='submit'>Log in</Button>
          </Form>
          <FormFooter>
            <StyledLink to='/auth/password/forgot-password'>
              {pageLanguage.forgotPassowrd}
            </StyledLink>
            <Divider />
            <Paragraph>
              Don&apos;t have an account?&nbsp;
              <StyledLink to='/auth/signup'>{pageLanguage.signUp}</StyledLink>
            </Paragraph>
          </FormFooter>
        </MainContent>
      </Main>
    </Base>
  )
}

LoginForm.propTypes = {
  isUserNew: PropTypes.bool
}

LoginForm.defaultValues = {
  isUserNew: false
}

export default LoginForm
